'use client';

import Link from 'next/link';
import React from 'react';

import { aboutSectionAssets } from '@/assets/about-section';
import { CoreItem, CoreLaunchPadCard } from '@/components/bases/CoreLaunchPadCard';
import HexagonalButton from '@/components/bases/HexagonalButton';

// Extract assets for easier use
const imgUntitled22 = aboutSectionAssets.untitled22;
const imgVuesaxBulkTag2 = aboutSectionAssets.tagIcon;
const imgFingerprintScan = aboutSectionAssets.fingerprintIcon;
const imgVuesaxBulkGlobal = aboutSectionAssets.globalIcon;
const imgIcon = aboutSectionAssets.serviceIcon;

// Reusable bullet components from page.tsx
const BulletWithIcon: React.FC<{ children: React.ReactNode; icon?: string }> = ({ children, icon }) => (
  <div className='flex items-center gap-3 md:gap-6'>
    {icon && (
      <div className='relative h-[18px] w-[18px] shrink-0 md:h-6 md:w-6'>
        <img src={icon} alt='' className='h-full w-full' />
      </div>
    )}
    <p className='font-["Gotham:Book",sans-serif] leading-5 text-white text-sm md:text-lg md:leading-6'>{children}</p>
  </div>
);

interface ProductCardProps {
  icon: string;
  title: string;
  description: string;
  href: string;
  className?: string;
}

const ProductCard: React.FC<ProductCardProps> = ({ icon, title, description, href, className }) => {
  const item: CoreItem = {
    id: 0,
    icon: icon,
  };

  return (
    <div
      className={`flex flex-1 flex-col items-center gap-6 rounded md:border-solid border border-white/10 p-6 md:flex-row md:items-start md:gap-6 md:px-10 md:py-6 ${className}`}>
      <CoreLaunchPadCard item={item} alignItems='flex-start' gap={0} width='fit-content' />
      <div className='flex flex-1 flex-col gap-4 text-center md:text-left'>
        <Link href={href} target='_blank' rel='noopener noreferrer'>
          <h3 className='font-["Gotham:Bold",sans-serif] leading-normal text-white text-lg hover:underline md:font-["Poppins:SemiBold",sans-serif] md:text-xl md:leading-[22px]'>
            {title}
          </h3>
        </Link>
        <p className='hidden font-["Gotham:Book",sans-serif] leading-6 text-white/65 mix-blend-plus-lighter opacity-65 md:block md:text-lg'>
          {description}
        </p>
      </div>
    </div>
  );
};

export const AboutSection: React.FC = () => {
  return (
    <section className='relative w-full bg-[#060912] py-20 md:py-32'>
      <div className='absolute inset-0 h-full w-full'>
        <img alt='' className='absolute block h-full w-full max-w-none' src={aboutSectionAssets.subtract} />
      </div>

      <div className='relative z-10 mx-auto max-w-[1280px] px-6'>
        <div className='mb-14 flex flex-col gap-8 md:mb-20 md:flex-row md:items-start md:gap-16'>
          <div className='relative h-[309px] w-full shrink-0 md:h-[492px] md:w-[520px]'>
            <div className='absolute inset-0 overflow-hidden pointer-events-none'>
              <img
                alt='LFG Incubator'
                className='absolute h-[103.77%] left-[-3.82%] max-w-none top-[-3.77%] w-[103.82%]'
                src={imgUntitled22}
              />
            </div>
          </div>

          <div className='flex flex-1 flex-col gap-8'>
            <h2 className='bg-gradient-to-r from-[#a9a9a9] via-white to-[#a9a9a9] bg-clip-text font-["Gotham:Bold",sans-serif] text-2xl leading-8 text-transparent md:text-5xl md:leading-[68px]'>
              INCUBATING THE FUTURE OF TECHNOLOGY
            </h2>
            <div className='font-["Gotham:Book",sans-serif] leading-5 text-white/65 mix-blend-plus-lighter opacity-65 text-sm md:text-lg md:leading-6'>
              <p className='mb-0'>
                LFG is a full-service project foundry and incubator focused on accelerating the growth of high-potential
                products and companies.
              </p>
              <p className='mb-0'>&nbsp;</p>
              <p>
                Our mission is to empower founders and teams with the execution power, structure, and guidance needed to
                build, launch, and scale successful digital products.
              </p>
            </div>
          </div>
        </div>

        <div className='mb-14 flex flex-col gap-14 md:mb-20 md:gap-14'>
          <h2 className='bg-gradient-to-r from-[#a9a9a9] via-white to-[#a9a9a9] bg-clip-text text-center font-["Gotham:Bold",sans-serif] text-2xl leading-8 text-transparent md:text-5xl md:leading-[68px]'>
            OUR SOLUTIONS INCLUDE
          </h2>

          <div className='flex flex-col gap-10'>
            <h3 className='font-["Gotham:Bold",sans-serif] text-2xl leading-8 text-white md:text-4xl md:leading-[48px]'>
              Products
            </h3>

            <div className='flex flex-col gap-6 md:flex-row md:gap-8'>
              <div className='grid grid-cols-1 md:grid-cols-2  md:gap-4'>
                <ProductCard
                  icon={imgVuesaxBulkTag2}
                  title='Core Sales Launchpad'
                  href='https://launch.lfg.inc/'
                  description='A strategic capital-raising platform engineered to launch initiatives and cultivate sustainable, high-performing communities.'
                />
                {/* <ProductCard
                  icon={imgFingerprintScan}
                  title='Digital Identity'
                  href='https://get.unstoppabledomains.com/lfg/'
                  description='A scalable digital identity solution designed to simplify authentication, secure digital ownership, and unify online presence.'
                /> */}
                <ProductCard
                  className='max-md:col-span-2'
                  icon={imgVuesaxBulkGlobal}
                  title='Global Workforce Platform'
                  href='https://orbit.lfg.inc/'
                  description='A unified hiring and payroll platform designed to support global teams with operational clarity and compliance.'
                />
              </div>
            </div>

            <div className='flex justify-center'>
              <Link href='/products'>
                <HexagonalButton variant='secondary' label='DISCOVER OUR PRODUCTS' width={316} />
              </Link>
            </div>
          </div>

          <div className='flex flex-col gap-10'>
            <h3 className='font-["Gotham:Bold",sans-serif] text-2xl leading-8 text-white md:text-4xl md:leading-[48px]'>
              Services
            </h3>

            <div className='flex flex-col gap-6 md:flex-row md:gap-20'>
              <p className='flex-1 font-["Gotham:Book",sans-serif] leading-5 text-white/65 mix-blend-plus-lighter opacity-65 text-sm md:text-lg md:leading-6'>
                Execution-driven services designed to accelerate product growth, expand market presence, and strengthen
                operational performance.
              </p>

              <div className='flex flex-col gap-4 md:flex-row md:gap-12'>
                <div className='flex flex-col gap-4'>
                  <BulletWithIcon icon={imgIcon}>Advisory Services</BulletWithIcon>
                  <BulletWithIcon icon={imgIcon}>Community Management</BulletWithIcon>
                  <BulletWithIcon icon={imgIcon}>Customer Support & Education</BulletWithIcon>
                  <BulletWithIcon icon={imgIcon}>Live Streaming Services</BulletWithIcon>
                </div>
                <div className='flex flex-col gap-4'>
                  <BulletWithIcon icon={imgIcon}>Market Making Services</BulletWithIcon>
                  <BulletWithIcon icon={imgIcon}>Exchange Listing Strategy</BulletWithIcon>
                  <BulletWithIcon icon={imgIcon}>Development Services</BulletWithIcon>
                  <BulletWithIcon icon={imgIcon}>Marketing Services</BulletWithIcon>
                </div>
              </div>
            </div>

            <div className='flex justify-center'>
              <Link href='/services'>
                <HexagonalButton variant='primary' label='VIEW OUR SERVICES' width={264} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
